import { useMatchRoute as useMatchRouteOriginal } from '@tanstack/react-router';
import { SentryTeamNames } from '@frontend/sentry-teams';
import { router, type RouterIds, type RouteById, type RouterType } from './router';
/** TODO: allowing this here to help with the migration:
 *    - Some old code will need to migrate to non-updated routes, which will cause the new hooks to complain
 *    - once all routes are migrated, let's remove this
 */
type AnyRoute = any;
type AnySearch<_T extends Record<string, string> = any> = any;
type AnyParams = Record<string, string>;
type SearchParams<T extends Record<string, string>> = T;

type RouteParams<T extends RouterIds> = RouteById<T>['types']['params'];
type RouteSearch<T extends RouterIds> = RouteById<T>['types']['searchSchema'];

export type {
  RouterType,
  RouteById,
  RouterIds,
  AnyRoute,
  AnySearch,
  AnyParams,
  SearchParams,
  RouteParams,
  RouteSearch,
};
// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface StaticDataRouteOption {
    team?: SentryTeamNames;
  }
}

// Custom, safer implementation or wrapper around the original hook.
// The default behavior to throw is too much of a foot-gun
const useMatchRoute: typeof useMatchRouteOriginal = (() => {
  const result = useMatchRouteOriginal();
  const override: typeof result = (...params) => {
    try {
      return result(...params);
    } catch {
      console.error('No Matching route found');
      return false;
    }
  };
  return override;
}) as typeof useMatchRouteOriginal;

export { router, useMatchRoute };
export { Route as RootRoute } from './routes/__root';
export { reloadPage } from './utilities/navigation-utils';

// By re exporting the api from TanStack router, we can enforce that other packages
// rely on this one instead, making the type register being applied
export {
  Outlet,
  Link,
  useRouteContext,
  useRouter,
  useRouterState,
  RouterProvider,
  getRouteApi,
  ErrorComponent,
  createLazyRoute,
  useBlocker,
  useNavigate,
  useSearch,
  useLocation,
  useMatch,
  useMatches,
  useParams,
  useLoaderData,
  createRootRouteWithContext,
  Navigate,
  LazyRoute,
  createMemoryHistory,
  Match,
  notFound,
} from '@tanstack/react-router';
export type { ErrorComponentProps, RegisteredRouter, ParsedLocation, NavigateOptions } from '@tanstack/react-router';
